"use client";

import { Fragment, useEffect, useState } from "react";
import { useRouter } from "@/hooks/useCompatRouter";

import Body from "@/containers/category/Body";
import Top from "@/containers/category/Top";
import useFetch from "@/hooks/useFetch";
import useProductsFilter from "@/hooks/useProductsFilter";
import { getBrandsByCategory } from "@/requests/brand";
import {
  getCategoryDetailsBySlug,
  getCategoryListLayer,
} from "@/requests/category";
import {
  getPriceMinMaxByFilter,
  getProductsByFilter,
} from "@/requests/product";
import { currentPageQuery } from "@/utils/constants";

const perPage = 20;

export interface CategoryViewProps {
  category: CategoryItem;
  products: ProductItemV2[];
  lowestPrice: number;
  highestPrice: number;
  totalProducts: number;
  builderPage: BuilderPage | null;
}

const CategoryView = ({
  category: _category,
  products: _products,
  lowestPrice: _lowestPrice,
  highestPrice: _highestPrice,
  totalProducts: _totalProducts,
  builderPage,
}: CategoryViewProps) => {
  const router = useRouter();

  const productsFilter = useProductsFilter(builderPage?.widgets);

  const [category, setCategory] = useState<CategoryItem>(_category);

  const [products, setProducts] = useState<ProductItemV2[]>(_products || []);
  const [lowestPrice, setLowestPrice] = useState<number>(_lowestPrice || 0);
  const [highestPrice, setHighestPrice] = useState<number>(_highestPrice || 0);
  const [totalProducts, setTotalProducts] = useState<number>(
    _totalProducts | 0,
  );
  const brands = useFetch(
    () =>
      getBrandsByCategory(category.slug || "", {
        perPage: 200,
        currentPage: 1,
        sortBy: "serial_no",
        sortOrder: "ASC",
      }),
    { deps: [category.slug] },
  );
  const subcategory = useFetch(
    () =>
      getCategoryListLayer({
        parentId: category.id,
        perPage: 100,
        currentPage: 1,
      }),
    { deps: [category.id] },
  );

  const [loading, setLoading] = useState<boolean>(true);

  const widgets = builderPage?.widgets || [];

  useEffect(() => {
    const slug = router.query.slug;
    getCategoryDetailsBySlug(slug?.toString() || "").then((res) => {
      setCategory((prev) => {
        if (prev.slug === res.slug) {
          return prev;
        }

        return res;
      });
    });
  }, [router.query.slug]);

  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(() => {
      const {
        slug,
        [currentPageQuery]: currentPage,
        ...restQuery
      } = router.query;

      if (router.query.slug) {
        getProductsByFilter({
          currentPage: productsFilter.activePage,
          perPage: productsFilter.perPage || perPage,
          filter: `&category=${router.query.slug}`,
          query: restQuery,
        })
          .then((res) => {
            setProducts(res.data);
            setTotalProducts(res.totalResult);
          })
          .finally(() => {
            setLoading(false);
          });
        getPriceMinMaxByFilter({
          currentPage: productsFilter.activePage,
          perPage: productsFilter.perPage || perPage,
          filter: `&category=${router.query.slug}`,
          query: restQuery,
        })
          .then((res) => {
            setLowestPrice(res.lowestPrice);
            setHighestPrice(res.highestPrice);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, 150);

    return () => {
      clearTimeout(timeout);
    };
  }, [router.query, productsFilter.perPage, productsFilter.activePage]);

  const breadcrumbs: { name: string; link?: Link }[] =
    category.breadcrumb?.map((item) => ({
      name: item.name,
      link: { urlType: "custom", url: `/category/${item.slug}` },
    })) || [];

  return (
    <Fragment key={(category.slug?.toString() || "") + "-category-page"}>
      <Top
        key={(category.slug?.toString() || "") + "-category-top"}
        brands={brands.response?.data || []}
        category={category}
        onFilter={productsFilter.handleFilter}
        breadcrumbs={[
          { name: "Home", link: { urlType: "custom", url: "/" } },
          ...breadcrumbs,
        ]}
        productsFilter={productsFilter}
        widgets={builderPage?.topbar || []}
        subcategories={subcategory.response || []}
      />
      <Body
        key={(category.slug?.toString() || "") + "-category-body"}
        products={products}
        lowestPrice={lowestPrice}
        highestPrice={highestPrice}
        category={category}
        subcategories={subcategory.response || []}
        onFilter={productsFilter.handleFilter}
        onUrlFilter={productsFilter.handleFilterByURL}
        total={totalProducts}
        loading={loading}
        productsFilter={productsFilter}
        setActivePage={productsFilter.setActivePage}
        widgets={widgets}
        setPerPage={productsFilter.setPerPage}
        perPage={productsFilter.perPage}
      />
    </Fragment>
  );
};

export default CategoryView;
